import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from 'src/app-redux/rtkq.interceptor';
import { partnerVerificationsTag } from 'src/app-redux/rtkq.tags';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import { apiResources } from 'src/common/Constants';
import { PartnerVerificationConfig } from 'src/configuration/PartnerVerificationConfig/PartnerVerificationConfig';
import {
  PartnerVerificationDataArgs,
  UpdateDocumentArgs,
  UpdatePartnerVerificationArgs,
  UploadPartnerVerificationDataArgs,
  VerifyPartnerVerificationDataArgs,
} from 'src/management/PartnerDocuments';
import { documentsApi } from './documentsAPI';

const verificationConfigTag = 'verificationConfig';
const verificationDataTag = 'verificationData';

/** RTK Query API slice for partner verification management endpoints. */
export const partnerVerificationsApi = createApi({
  reducerPath: 'partnerVerificationsApi',
  tagTypes: [verificationConfigTag, verificationDataTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch partner verification configuration. */
    getVerificationConfig: builder.query<Array<PartnerVerificationConfig>, void>({
      providesTags: [verificationConfigTag],
      query: () => `${apiResources.partnerVerificationConfig}`,
      ...transformResponses,
    }),
    /** Mutation to submit partner verification configuration. */
    submitVerificationConfig: builder.mutation<void, { body: Array<PartnerVerificationConfig> }>({
      query: ({ body }) => ({
        url: `${apiResources.partnerVerificationConfig}`,
        method: 'POST',
        body,
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [verificationConfigTag]),
    }),
    /** Mutation to update partner verification status. */
    updatePartnerVerification: builder.mutation<string, UpdatePartnerVerificationArgs>({
      query: (body) => ({
        url: apiResources.updateUserVerfication,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      ...transformResponses,
    }),
    /** Mutation to update a partner verification document. */
    updateVerificationDocument: builder.mutation<string, UpdateDocumentArgs>({
      query: (body) => ({
        url: apiResources.partnerVerificationUpdateDocument,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(documentsApi.util.invalidateTags([partnerVerificationsTag]));
      },
      ...transformResponses,
    }),
    /** Query to fetch partner verification data by partner ID. */
    getPartnerVerificationData: builder.query<Array<PartnerVerificationDataArgs>, number>({
      providesTags: [verificationDataTag],
      query: (partnerId: number) => `${apiResources.partnerVerificationData}/${partnerId}`,
      ...transformResponses,
    }),
    /** Mutation to upload partner verification data. */
    uploadPartnerVerificationData: builder.mutation<string, UploadPartnerVerificationDataArgs>({
      query: (body) => ({
        url: apiResources.partnerVerificationData,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [verificationDataTag]),
    }),
    /** Mutation to verify partner verification data. */
    verifyPartnerVerificationData: builder.mutation<string, VerifyPartnerVerificationDataArgs>({
      query: (body) => ({
        url: apiResources.partnerVerificationDataVerify,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      ...transformResponses,
      invalidatesTags: (_result, error) => (error ? [] : [verificationDataTag]),
    }),
  }),
});

/** RTK Query hooks for partner verification management endpoints. */
export const {
  useGetVerificationConfigQuery,
  useSubmitVerificationConfigMutation,
  useUpdatePartnerVerificationMutation,
  useUpdateVerificationDocumentMutation,
  useGetPartnerVerificationDataQuery,
  useUploadPartnerVerificationDataMutation,
  useVerifyPartnerVerificationDataMutation,
} = partnerVerificationsApi;
